import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import {
  cancelAllAttractionNotifications,
  cancelAttractionNotification,
  createAttractionNotification,
  getAttractionNotificationState,
  getExistingPushSubscription,
  hashPushEndpoint,
  subscribeBrowserToPush,
  unsubscribeCurrentAttractionNotificationDevice,
} from '../services/attractionNotificationApi';
import AttractionNotificationModal from '../components/attraction-notifications/AttractionNotificationModal';
import {
  equivalentAttractionNotificationParkSlug,
  resolveAttractionNotificationParkSlug,
} from '../utils/attractionNotificationParkSlug';
import {
  hasNativePush,
  requestNativePushToken,
} from '../utils/nativeCapabilities';
import { isIOS, isStandalonePwa } from '../utils/deviceDetection';
import '../styles/components/attraction-notifications.css';

const AttractionNotificationContext = createContext(null);
const ACTIVE_ALERT_LIMIT_ERROR_CODE = 'ACTIVE_ALERT_LIMIT_REACHED';
const INVALID_ALERT_THRESHOLD_ERROR_CODE = 'INVALID_ALERT_THRESHOLD';
const ALERT_NOT_FOUND_ERROR_CODE = 'ALERT_NOT_FOUND';

const getDeviceSupport = (nativePushAvailable = hasNativePush()) => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { supported: false, reason: 'unavailable' };
  }

  if (nativePushAvailable) {
    return {
      supported: true,
      reason: null,
      isAndroidWrapper: true,
      transport: 'fcm',
    };
  }

  const iosDevice = isIOS();
  const isStandalone = isStandalonePwa();

  if (iosDevice && !isStandalone) {
    return { supported: false, reason: 'ios_pwa_required', isIOS: iosDevice, isStandalone };
  }

  if (!window.isSecureContext && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return { supported: false, reason: 'secure_context_required', isIOS: iosDevice, isStandalone };
  }

  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { supported: false, reason: 'unsupported', isIOS: iosDevice, isStandalone };
  }

  return { supported: true, reason: null, isIOS: iosDevice, isStandalone, transport: 'web_push' };
};

const normalizeText = (value, fallback = '') => {
  const text = String(value || '').trim();
  return text || fallback;
};

const getAttractionId = (attraction = {}) => normalizeText(
  attraction.id || attraction.externalId || attraction.external_id || attraction.api_id || attraction.code || attraction.uuid
);

const getAttractionWaitTime = (attraction = {}) => {
  const candidates = [
    attraction.wait_time,
    attraction.waitTime,
    attraction.waitingtime,
    attraction.queue?.STANDBY?.waitTime,
    attraction.queue?.standby?.waitTime,
  ];

  for (const value of candidates) {
    const number = Number(value);
    if (Number.isFinite(number) && number >= 0) return Math.round(number);
  }

  return null;
};

const getAttractionStatus = (attraction = {}) => normalizeText(
  attraction.status || attraction.state || attraction.availability || (attraction.is_open === true ? 'OPERATING' : '')
).toUpperCase();

const isAttractionDown = (attraction = {}) => {
  const status = getAttractionStatus(attraction);
  return ['DOWN', 'BREAKDOWN', 'BROKEN', 'TEMPORARILY_CLOSED'].some((term) => status.includes(term));
};

const createActiveAlertLimitError = (maxActiveAlerts, activeCount) => {
  const error = new Error('Limite d’alertes atteinte');
  error.code = ACTIVE_ALERT_LIMIT_ERROR_CODE;
  error.details = {
    error: ACTIVE_ALERT_LIMIT_ERROR_CODE,
    maxActiveAlerts,
    activeCount,
  };
  return error;
};

const normalizeActionError = (error, t) => {
  const code = error?.code || error?.details?.error || null;

  if (code === ACTIVE_ALERT_LIMIT_ERROR_CODE) {
    const maxActiveAlerts = Number(error?.details?.maxActiveAlerts || error?.maxActiveAlerts || 1);
    const activeCount = Number(error?.details?.activeCount || error?.activeCount || maxActiveAlerts);
    return {
      code,
      maxActiveAlerts,
      activeCount,
      message: t(
        'attractionNotifications.limit.reached',
        'Tu as atteint la limite de ton niveau actuel ({{max}} alerte active). Annule une alerte ou passe à {{tier}} pour en activer davantage.',
        {
          max: maxActiveAlerts,
          tier: maxActiveAlerts < 3
            ? t('attractionNotifications.plans.vip.label', 'VIP')
            : t('attractionNotifications.plans.supporter.label', 'Membre soutien'),
        }
      ),
    };
  }

  const rawMessage = String(error?.details?.message || error?.message || '');
  if (code === INVALID_ALERT_THRESHOLD_ERROR_CODE || /Seuil de notification invalide/i.test(rawMessage)) {
    return {
      code: INVALID_ALERT_THRESHOLD_ERROR_CODE,
      message: t(
        'attractionNotifications.customThreshold.serverError',
        'Choisis un temps d’attente entre 1 et 180 minutes pour créer cette alerte.'
      ),
    };
  }

  return {
    code,
    message: rawMessage || t('attractionNotifications.modal.createError', 'Impossible de créer cette alerte. Réessaie dans un instant.'),
  };
};

const isNotificationServerReadyForTransport = (state = {}, support = {}) => {
  if (!state.serverReady) return false;
  if (support.transport === 'fcm') return state.fcmReady !== false;
  if (support.transport === 'web_push') return state.webPushReady !== false && Boolean(state.vapidPublicKey);
  return Boolean(state.serverReady);
};

export const AttractionNotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [nativePushAvailable, setNativePushAvailable] = useState(() => hasNativePush());
  const notificationsEnabled = true;

  const [activeAlerts, setActiveAlerts] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [currentDeviceSubscribed, setCurrentDeviceSubscribed] = useState(false);
  const [serverReady, setServerReady] = useState(false);
  const [serverVapidPublicKey, setServerVapidPublicKey] = useState(null);
  const [maxActiveAlerts, setMaxActiveAlerts] = useState(1);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [selectedContext, setSelectedContext] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const refreshNativeCapabilities = () => {
      setNativePushAvailable(hasNativePush());
    };

    refreshNativeCapabilities();
    window.addEventListener('mpt-native-app', refreshNativeCapabilities);
    return () => window.removeEventListener('mpt-native-app', refreshNativeCapabilities);
  }, []);

  const support = useMemo(() => getDeviceSupport(nativePushAvailable), [nativePushAvailable]);

  const getCurrentEndpointHash = useCallback(async () => {
    try {
      if (hasNativePush()) {
        const subscription = await requestNativePushToken();
        return subscription?.token ? hashPushEndpoint(`fcm:${subscription.token}`) : null;
      }

      const subscription = await getExistingPushSubscription();
      return subscription?.endpoint ? hashPushEndpoint(`web_push:${subscription.endpoint}`) : null;
    } catch {
      return null;
    }
  }, []);

  const refreshState = useCallback(async () => {
    if (!user) {
      setActiveAlerts([]);
      setSubscriptions([]);
      setCurrentDeviceSubscribed(false);
      setServerReady(false);
      setServerVapidPublicKey(null);
      setMaxActiveAlerts(1);
      setActionError(null);
      return null;
    }

    setLoading(true);
    try {
      const endpointHash = await getCurrentEndpointHash();
      const state = await getAttractionNotificationState(endpointHash);
      setActiveAlerts(Array.isArray(state.activeAlerts) ? state.activeAlerts : []);
      setSubscriptions(Array.isArray(state.subscriptions) ? state.subscriptions : []);
      setCurrentDeviceSubscribed(Boolean(state.currentDeviceSubscribed));
      setServerReady(isNotificationServerReadyForTransport(state, support));
      setServerVapidPublicKey(state.vapidPublicKey || null);
      setMaxActiveAlerts(state.maxActiveAlerts || 1);
      return state;
    } catch (err) {
      setError(err.message || 'Impossible de charger les alertes.');
      setActionError(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getCurrentEndpointHash, support, user]);

  useEffect(() => {
    refreshState();
  }, [refreshState]);

  const buildContext = useCallback((input = {}) => {
    const attraction = input.attraction || {};
    const rawParkSlug = normalizeText(input.parkSlug || attraction.parkSlug || attraction.park_slug || input.parkId || attraction.parkId);
    const parkSlug = resolveAttractionNotificationParkSlug(rawParkSlug, location.pathname);
    const attractionId = getAttractionId(attraction);

    return {
      attraction,
      parkSlug,
      parkId: normalizeText(input.parkId || attraction.parkId || parkSlug),
      parkName: normalizeText(input.parkName || attraction.parkName || parkSlug),
      attractionId,
      attractionName: normalizeText(input.attractionName || attraction.name || attraction.title, attractionId),
      status: getAttractionStatus(attraction),
      waitTime: getAttractionWaitTime(attraction),
      isDown: input.isDown ?? isAttractionDown(attraction),
      url: input.url || `${location.pathname}${location.search || ''}`,
    };
  }, [location.pathname, location.search]);

  const getAlertForAttraction = useCallback((parkSlug, attractionId) => {
    const normalizedParkSlug = normalizeText(parkSlug);
    const normalizedAttractionId = normalizeText(attractionId);
    return activeAlerts.find((alert) =>
      alert.status === 'active'
      && equivalentAttractionNotificationParkSlug(alert.parkSlug, normalizedParkSlug, location.pathname)
      && alert.attractionId === normalizedAttractionId
    ) || null;
  }, [activeAlerts, location.pathname]);

  const openAttractionNotification = useCallback((input) => {
    const context = buildContext(input);
    if (!user) {
      const returnTo = context.url || `${location.pathname}${location.search || ''}`;
      navigate(`/notifications?returnTo=${encodeURIComponent(returnTo)}`);
      return;
    }

    setError(null);
    setActionError(null);
    setSelectedContext(context);
    setModalOpen(true);
  }, [buildContext, location.pathname, location.search, navigate, user]);

  useEffect(() => {
    if (!user) {
      setModalOpen(false);
      setSelectedContext(null);
      setError(null);
      setActionError(null);
    }
  }, [user]);

  const closeAttractionNotification = useCallback(() => {
    setModalOpen(false);
    setSelectedContext(null);
    setError(null);
    setActionError(null);
  }, []);

  const goToSignup = useCallback(() => {
    const redirect = encodeURIComponent(`${location.pathname}${location.search || ''}`);
    closeAttractionNotification();
    navigate(`/signup?redirect=${redirect}`);
  }, [closeAttractionNotification, location.pathname, location.search, navigate]);

  const goToLogin = useCallback(() => {
    const redirect = encodeURIComponent(`${location.pathname}${location.search || ''}`);
    closeAttractionNotification();
    navigate(`/login?redirect=${redirect}`);
  }, [closeAttractionNotification, location.pathname, location.search, navigate]);

  const goToNotifications = useCallback((plan = null) => {
    const returnTo = selectedContext?.url || `${location.pathname}${location.search || ''}`;
    const params = new URLSearchParams({ returnTo });
    if (plan) params.set('plan', plan);
    closeAttractionNotification();
    navigate(`/notifications?${params.toString()}`);
  }, [closeAttractionNotification, location.pathname, location.search, navigate, selectedContext?.url]);

  const ensurePushSubscription = useCallback(async () => {
    if (!support.supported) {
      const supportError = new Error('Notifications non disponibles sur cet appareil.');
      supportError.code = support.reason;
      throw supportError;
    }

    if (support.transport === 'fcm' || hasNativePush()) {
      try {
        return await requestNativePushToken();
      } catch (err) {
        const nativeError = new Error('Notifications Android indisponibles.');
        nativeError.code = err.code || 'ANDROID_PUSH_UNAVAILABLE';
        nativeError.cause = err;
        throw nativeError;
      }
    }

    const notificationApi = window.Notification;

    if (notificationApi.permission === 'denied') {
      const permissionError = new Error('Les notifications sont bloquées dans le navigateur.');
      permissionError.code = 'PERMISSION_DENIED';
      throw permissionError;
    }

    if (notificationApi.permission !== 'granted') {
      const permission = await notificationApi.requestPermission();
      if (permission !== 'granted') {
        const permissionError = new Error('Autorisation de notification refusée.');
        permissionError.code = 'PERMISSION_DENIED';
        throw permissionError;
      }
    }

    return subscribeBrowserToPush(serverVapidPublicKey);
  }, [serverVapidPublicKey, support]);

  const createAlert = useCallback(async ({ thresholdMinutes, type }) => {
    if (!selectedContext) return null;

    setActionLoading(true);
    setError(null);
    setActionError(null);
    try {
      if (activeAlerts.length >= maxActiveAlerts) {
        throw createActiveAlertLimitError(maxActiveAlerts, activeAlerts.length);
      }

      const subscription = await ensurePushSubscription();
      const serializedSubscription = subscription?.transport === 'fcm'
        ? {
            transport: 'fcm',
            token: subscription.token,
            platform: subscription.platform || 'android',
            installationId: subscription.installationId,
            appVersion: subscription.appVersion,
          }
        : subscription.toJSON();
      const result = await createAttractionNotification({
        subscription: serializedSubscription,
        language: i18n.language,
        deviceLabel: serializedSubscription.transport === 'fcm'
          ? 'Android app'
          : (navigator.userAgentData?.platform || navigator.platform || null),
        alert: {
          parkSlug: selectedContext.parkSlug,
          parkId: selectedContext.parkId,
          parkName: selectedContext.parkName,
          attractionId: selectedContext.attractionId,
          attractionName: selectedContext.attractionName,
          type,
          thresholdMinutes,
          currentStatus: selectedContext.status,
          currentWaitTime: selectedContext.waitTime,
          url: selectedContext.url,
        },
      });

      await refreshState();
      return result;
    } catch (err) {
      const normalizedError = normalizeActionError(err, t);
      setActionError(normalizedError);
      setError(normalizedError.message);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [activeAlerts.length, ensurePushSubscription, i18n.language, maxActiveAlerts, refreshState, selectedContext, t]);

  const cancelAlert = useCallback(async (alertId) => {
    setActionLoading(true);
    setError(null);
    setActionError(null);
    try {
      const result = await cancelAttractionNotification(alertId);
      setActiveAlerts((currentAlerts) => currentAlerts.filter((alert) => alert.id !== alertId));
      await refreshState();
      if (result?.notFound) {
        setError(t(
          'attractionNotifications.panel.alertNotFound',
          'Cette alerte n’existe plus. La liste a été actualisée.'
        ));
      }
      return result;
    } catch (err) {
      const code = err?.code || err?.details?.error;
      if (code === ALERT_NOT_FOUND_ERROR_CODE) {
        setActiveAlerts((currentAlerts) => currentAlerts.filter((alert) => alert.id !== alertId));
        await refreshState();
        setError(t(
          'attractionNotifications.panel.alertNotFound',
          'Cette alerte n’existe plus. La liste a été actualisée.'
        ));
        return { success: true, alert: null, notFound: true };
      }
      setError(err.message || 'Impossible d’annuler cette alerte.');
      setActionError(null);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [refreshState, t]);

  const cancelAllAlerts = useCallback(async () => {
    setActionLoading(true);
    setError(null);
    setActionError(null);
    try {
      await cancelAllAttractionNotifications();
      await refreshState();
    } catch (err) {
      setError(err.message || 'Impossible d’annuler les alertes.');
      setActionError(null);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [refreshState]);

  const unsubscribeCurrentDevice = useCallback(async () => {
    setActionLoading(true);
    setError(null);
    setActionError(null);
    try {
      if (hasNativePush()) {
        const subscription = await requestNativePushToken();
        await unsubscribeCurrentAttractionNotificationDevice({
          transport: 'fcm',
          token: subscription.token,
          platform: subscription.platform || 'android',
          installationId: subscription.installationId,
          appVersion: subscription.appVersion,
        });
        await refreshState();
        return;
      }

      const subscription = await getExistingPushSubscription();
      if (subscription) {
        await unsubscribeCurrentAttractionNotificationDevice(subscription.toJSON());
        await subscription.unsubscribe().catch(() => false);
      }
      await refreshState();
    } catch (err) {
      setError(err.message || 'Impossible de désabonner cet appareil.');
      setActionError(null);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [refreshState]);

  const value = useMemo(() => ({
    actionLoading,
    actionError,
    activeAlerts,
    cancelAlert,
    cancelAllAlerts,
    closeAttractionNotification,
    createAlert,
    currentDeviceSubscribed,
    enabled: notificationsEnabled,
    error,
    getAlertForAttraction,
    goToNotifications,
    loading,
    maxActiveAlerts,
    openAttractionNotification,
    refreshState,
    serverReady,
    subscriptions,
    support,
    unsubscribeCurrentDevice,
  }), [
    actionLoading,
    actionError,
    activeAlerts,
    cancelAlert,
    cancelAllAlerts,
    closeAttractionNotification,
    createAlert,
    currentDeviceSubscribed,
    notificationsEnabled,
    error,
    getAlertForAttraction,
    goToNotifications,
    loading,
    maxActiveAlerts,
    openAttractionNotification,
    refreshState,
    serverReady,
    subscriptions,
    support,
    unsubscribeCurrentDevice,
  ]);

  const selectedAlert = selectedContext
    ? getAlertForAttraction(selectedContext.parkSlug, selectedContext.attractionId)
    : null;

  return (
    <AttractionNotificationContext.Provider value={value}>
      {children}
      <AttractionNotificationModal
        activeAlert={selectedAlert}
        context={selectedContext}
        isOpen={modalOpen}
        onClose={closeAttractionNotification}
        onLogin={goToLogin}
        onSignup={goToSignup}
        onUpgrade={goToNotifications}
        user={user}
      />
    </AttractionNotificationContext.Provider>
  );
};

export const useAttractionNotifications = () => {
  const context = useContext(AttractionNotificationContext);
  if (!context) {
    throw new Error('useAttractionNotifications must be used inside AttractionNotificationProvider');
  }
  return context;
};
